import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { subscribeAdminUsers } from '../services/adminTicketingService';
import { formatTicketPriority, toJsDate } from '../../../features/support-tickets/utils/ticketFormatters';
import {
  adminAddSupportTicketMessage,
  adminGetSupportTicketAttachmentUrl,
  adminListSupportTicketMessages,
  adminListSupportTickets,
  adminUpdateSupportTicketAssignment,
  adminUpdateSupportTicketStatus,
} from '../services/adminTicketingFunctionsApi';
import { ADMIN_PATHS, adminPath, adminCompanyPath, adminUserPath } from '../../constants/adminPaths';

import { PageSkeleton } from '../../../components/loading/skeletons';
import '../styles/adminTicketing.css';

export default function AdminTicketingTicketPage() {
  const navigate = useNavigate();
  const { companyId, ticketId } = useParams();
  const location = useLocation();
  const { admin } = useAdminAuth();
  const getAdminIdTokenRef = useRef(null);

  const [ticket, setTicket] = useState(location.state?.ticket || null);
  const [admins, setAdmins] = useState(null);
  const [messages, setMessages] = useState(null);
  const [sending, setSending] = useState(false);
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getAdminIdTokenRef.current = admin?.getIdToken;
  }, [admin?.getIdToken]);

  useEffect(() => {
    const unsub = subscribeAdminUsers({
      onData: (items) => setAdmins(items),
      onError: (err) => console.error('Error loading admins:', err),
    });
    return () => unsub();
  }, []);

  // Load ticket if we didn't get it via navigation state
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (ticket) return;
        const data = await adminListSupportTickets({ status: 'all', pageSize: 250 }, getAdminIdTokenRef.current);
        const items = Array.isArray(data) ? data : [];
        const found = items.find((t) => t.id === ticketId && t.companyId === companyId);
        if (!cancelled) setTicket(found || null);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError('Could not load ticket.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, ticketId, ticket]);

  // Poll messages
  useEffect(() => {
    if (!companyId || !ticketId) return undefined;
    let cancelled = false;

    const fetchMessages = async () => {
      try {
        const data = await adminListSupportTicketMessages(
          { companyId, ticketId, pageSize: 250 },
          getAdminIdTokenRef.current
        );
        if (!cancelled) setMessages(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError('Could not load messages.');
      }
    };

    fetchMessages();
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      fetchMessages();
    }, 6000);

    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [companyId, ticketId]);

  const openAdminAttachment = async (attachment) => {
    try {
      const storagePath = attachment?.storagePath;
      if (!storagePath) return;
      const res = await adminGetSupportTicketAttachmentUrl({ storagePath }, getAdminIdTokenRef.current);
      const url = res?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error(e);
      setError('Could not open attachment.');
    }
  };

  const handleSend = async () => {
    const body = reply.trim();
    if (!body) return;
    setError('');
    setSending(true);
    try {
      const authorFirstName =
        admin?.firstName ||
        (typeof admin?.name === 'string' ? admin.name.split(/\s+/)[0] : '') ||
        (typeof admin?.displayName === 'string' ? admin.displayName.split(/\s+/)[0] : '') ||
        '';
      await adminAddSupportTicketMessage(
        { companyId, ticketId, body, authorFirstName },
        getAdminIdTokenRef.current
      );
      setReply('');
      // refresh messages quickly
      const data = await adminListSupportTicketMessages({ companyId, ticketId, pageSize: 250 }, getAdminIdTokenRef.current);
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (nextStatus) => {
    setError('');
    try {
      await adminUpdateSupportTicketStatus({ companyId, ticketId, status: nextStatus }, getAdminIdTokenRef.current);
      // optimistic update
      setTicket((prev) => (prev ? { ...prev, status: nextStatus } : prev));
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to update status.');
    }
  };

  const handleAssign = async (assigneeUid) => {
    setError('');
    try {
      const next = assigneeUid
        ? (admins || []).find((a) => a.id === assigneeUid || a.uid === assigneeUid) || { uid: assigneeUid }
        : null;
      await adminUpdateSupportTicketAssignment(
        {
          companyId,
          ticketId,
          assignedTo: next
            ? { uid: next.id || next.uid, email: next.email, name: next.name || `${next.firstName || ''} ${next.lastName || ''}`.trim() }
            : null,
        },
        getAdminIdTokenRef.current
      );
      setTicket((prev) =>
        prev
          ? {
              ...prev,
              assignedTo: next ? { uid: next.id || next.uid, email: next.email, name: next.name || `${next.firstName || ''} ${next.lastName || ''}`.trim() } : null,
              assignedToUid: next ? (next.id || next.uid) : null,
            }
          : prev
      );
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to update assignment.');
    }
  };

  const openUserProfile = () => {
    const uid = ticket?.createdBy?.uid;
    if (!uid) return;
    navigate(adminUserPath(uid));
  };

  const openCompanyProfile = () => {
    if (!companyId) return;
    navigate(adminCompanyPath(companyId));
  };

  const headerPriority = useMemo(() => formatTicketPriority(ticket?.priority), [ticket?.priority]);
  const headerPriorityClass = useMemo(() => {
    const p = ticket?.priority;
    const classes = ['at-badge', 'at-badgePriority'];
    if (p === 'low') classes.push('at-badgeLow');
    if (p === 'normal') classes.push('at-badgeNormal');
    if (p === 'high') classes.push('at-badgeHigh');
    if (p === 'urgent') classes.push('at-badgeUrgent');
    return classes.join(' ');
  }, [ticket?.priority]);

  const ticketDisplayNumber = useMemo(() => {
    if (typeof ticket?.ticketNumber === 'number') return `Ticket #${ticket.ticketNumber}`;
    if (typeof ticket?.ticketNumber === 'string' && ticket.ticketNumber.trim()) return `Ticket #${ticket.ticketNumber.trim()}`;
    if (ticketId) return `Ticket #${String(ticketId).slice(0, 6).toUpperCase()}`; // fallback for legacy tickets
    return 'Ticket';
  }, [ticket?.ticketNumber, ticketId]);

  return (
    <div className="admin-page">
      <div className="at-hero">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h2 className="at-heroTitle">Ticket</h2>
            <p
              className="at-heroSub"
              title={ticketId ? `Internal ID: ${ticketId}` : undefined}
            >
              Company: {ticket?.companyName || 'Unknown Company'} · {ticketDisplayNumber}
            </p>
          </div>
          <button type="button" className="at-btn" onClick={() => navigate(adminPath(ADMIN_PATHS.SUPPORT))}>
            Back to inbox
          </button>
        </div>
      </div>

      {error ? <div className="at-alert at-alertError" style={{ marginTop: 12 }}>{error}</div> : null}

      <div className="at-card" style={{ marginTop: 14 }}>
        <div className="at-cardHeader">
          <h3 className="at-cardTitle">{ticket?.subject || 'Ticket'}</h3>
          <span className={headerPriorityClass}>Priority: {headerPriority}</span>
        </div>

        <div className="at-controls">
          <div className="at-row2">
            <div>
              <label className="at-label" htmlFor="at-ticket-status">Status</label>
              <select
                id="at-ticket-status"
                className="at-select"
                value={ticket?.status || 'open'}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="at-label" htmlFor="at-assignee">Assignee</label>
              <select
                id="at-assignee"
                className="at-select"
                value={ticket?.assignedToUid || ''}
                onChange={(e) => handleAssign(e.target.value || null)}
              >
                <option value="">Unassigned</option>
                {(admins || []).map((a) => {
                  const name = (a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || a.id);
                  return (
                    <option key={a.id} value={a.id}>
                      {name} {a.email ? `(${a.email})` : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="at-btn" onClick={openCompanyProfile}>Open Company</button>
            <button type="button" className="at-btn" onClick={openUserProfile} disabled={!ticket?.createdBy?.uid}>Open User</button>
          </div>
        </div>

        <div className="at-thread">
          {!messages ? (
            <PageSkeleton showHeader={false} rows={5} label="Loading messages" />
          ) : messages.length === 0 ? (
            <div className="at-empty">No messages yet.</div>
          ) : (
            messages.map((m) => {
              const dt = toJsDate(m.createdAt);
              const nameBase = (m.author?.name || m.author?.email || (m.authorType === 'admin' ? 'Admin' : 'Company'));
              const firstName = typeof nameBase === 'string' ? (nameBase.split(' - ')[0].split(/\s+/)[0] || nameBase) : 'Support';
              return (
                <div key={m.id} className="at-message">
                  <div className="at-messageTop">
                    <div className="at-messageAuthor">
                      {m.authorType === 'admin' ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <span>{firstName}</span>
                          <span className="at-tag">Support</span>
                        </span>
                      ) : (
                        (m.author?.name || m.author?.email || 'Company')
                      )}
                    </div>
                    <div className="at-messageTime">{dt ? dt.toLocaleString() : '—'}</div>
                  </div>
                  <div className="at-messageBody">{m.body}</div>
                  {Array.isArray(m.attachments) && m.attachments.length > 0 ? (
                    <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {m.attachments.map((a, idx) => (
                        <button
                          key={`${a.storagePath || a.name || 'file'}-${idx}`}
                          type="button"
                          className="at-btn"
                          onClick={() => openAdminAttachment(a)}
                        >
                          {a.name || 'Attachment'}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>

        <div className="at-controls">
          <label className="at-label" htmlFor="at-reply">Reply</label>
          <textarea
            id="at-reply"
            className="at-textarea"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write a reply…"
            maxLength={4000}
          />
          <div className="at-actions" style={{ paddingLeft: 0, paddingRight: 0 }}>
            <button className="at-btn at-btnPrimary" type="button" onClick={handleSend} disabled={sending || reply.trim().length < 2}>
              {sending ? 'Sending…' : 'Send reply'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

